import type {
  Product,
  SelectedOption,
  ProductVariant,
  MediaConnection,
} from "@shopify/hydrogen-react/storefront-api-types";
import { useQuery, useQueryClient } from "react-query";
import { client } from "./client";

const PRODUCT_QUERY = `#graphql
  query product($handle: String!, $selectedOptions: [SelectedOptionInput!]!) {
    product(handle: $handle) {
      id
      availableForSale
      description
      descriptionHtml
      productType
      title
      vendor
      featuredImage {
        id
        altText
        height
        width
        url
      }
      artistId: metafield(namespace: "hug", key: "artist_id") {
        value
      }
      details: metafield(namespace: "hug", key: "product_details") {
        value
        reference {
          ... on Metaobject {
            handle
            fields {
              key
              value
            }
          }
        }
      }
      options {
        name,
        values
      }
      priceRange {
        maxVariantPrice {
          amount
          currencyCode
        }
        minVariantPrice {
          amount
          currencyCode
        }
      }
      selectedVariant: variantBySelectedOptions(selectedOptions: $selectedOptions) {
        id
        availableForSale
        quantityAvailable
        sku
        title
        selectedOptions {
          name
          value
        }
        image {
          id
          url
          altText
          width
          height
        }
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
          currencyCode
        }
      }
      variants(first: 2) {
        nodes {
          id
          availableForSale
          quantityAvailable
          sku
          title
          selectedOptions {
            name
            value
          }
          image {
            id
            url
            altText
            width
            height
          }
          price {
            currencyCode
            amount
          }
          compareAtPrice {
            currencyCode
            amount
          }
        }
      }
      media(first: 10) {
        nodes {
          id
          mediaContentType
          previewImage {
            altText
            height
            id
            width
            url
          }
        }
      }
    }
  }
`;

type VariantNode = Pick<
  ProductVariant,
  | "id"
  | "availableForSale"
  | "quantityAvailable"
  | "sku"
  | "title"
  | "image"
  | "price"
  | "compareAtPrice"
  | "selectedOptions"
>;

type ProductResp = Pick<
  Product,
  | "id"
  | "availableForSale"
  | "description"
  | "descriptionHtml"
  | "featuredImage"
  | "options"
  | "priceRange"
  | "productType"
  | "title"
  | "vendor"
> & {
  artistId?: { value: string };
  details?: {
    value: string;
    reference: {
      handle: string;
      fields: {
        key: string;
        value: string;
      };
    } | null;
  };
  selectedVariant?: VariantNode | null;
  variants: {
    nodes: VariantNode[];
  };
  media: MediaConnection;
};

const getProduct = (handle: string, selectedOptions: SelectedOption[]) =>
  client.query<{ product?: ProductResp }>(PRODUCT_QUERY, {
    handle,
    selectedOptions,
  });

const useProductQuery = (handle: string, options: SelectedOption[] = []) => {
  const queryClient = useQueryClient();

  return useQuery(
    ["product", "detail", handle, options],
    () => getProduct(handle, options),
    {
      placeholderData: () => {
        // Find placeholder data so we don't start with some product data when switching between
        // options and fetching new variants. This will find any existing queries for that product
        // regardless of options and "preload" that product data until the fetch completes and
        // overwrites this placeholder content with the newly fetched variant data.
        const priorQueries = queryClient.getQueriesData<{
          product?: ProductResp;
        }>(["product", "detail", handle]);
        return priorQueries.find((d) => !!d)?.[1];
      },
      select: (data) => {
        let details: string[];
        try {
          // Try to parse the stringified array from shopify, example:
          // "[\"foo\", \"bar\"]" => ["foo", "bar"]
          details = JSON.parse(data.product?.details?.value ?? "[]");
        } catch {
          details = [];
        }

        return {
          ...data.product,
          artistId: data.product?.artistId?.value,
          details,
          hasVariants: (data.product?.variants.nodes.length ?? 0) > 1,
          selectedVariant:
            data.product?.selectedVariant ?? data.product?.variants.nodes[0],
        };
      },
    },
  );
};

export { useProductQuery };
